import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Radio,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { FamilyMemberDuplicationCheckDTO } from '@/api/DTO/family/family.interface';
import { useFamilyDuplicationColumns } from './FamilyMemberDuplicateDialog';

export type ClosedFamilyMemberChoice =
	| { action: 'cancel' }
	| { action: 'skip' }
	| { action: 'link'; closedFamilyMember: FamilyMemberDuplicationCheckDTO };

interface Props {
	open: boolean;
	rows: FamilyMemberDuplicationCheckDTO[];
	onClose: (choice: ClosedFamilyMemberChoice) => void;
}

/**
 * DMS-REQ-WEB-CHA-004 §三: closed family members of this centre that may be
 * the same person. Staff link one, continue without linking, or cancel.
 */
export default function FamilyClosedDuplicateDialog({
	open,
	rows,
	onClose,
}: Props) {
	const { t } = useTranslation();
	const [selected, setSelected] =
		useState<FamilyMemberDuplicationCheckDTO | null>(null);
	const [confirming, setConfirming] = useState(false);
	const baseColumns = useFamilyDuplicationColumns();

	useEffect(() => {
		if (open) {
			setSelected(null);
			setConfirming(false);
		}
	}, [open]);

	const columns: GridColDef<FamilyMemberDuplicationCheckDTO>[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={selected?.id === params.row.id}
					onChange={() => setSelected(params.row)}
				/>
			),
		},
		...baseColumns,
	];

	return (
		<Dialog
			open={open}
			onClose={() => onClose({ action: 'cancel' })}
			maxWidth={confirming ? 'sm' : 'lg'}
			fullWidth
		>
			<DialogTitle variant='h5'>
				{confirming
					? t('memberDuplication.familyLinkConfirmTitle')
					: t('memberDuplication.familyClosedTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose({ action: 'cancel' })}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				{confirming ? (
					<Typography variant='body1'>
						{t('memberDuplication.familyLinkConfirmText', {
							fileNo: selected?.familyMemberFileNo,
						})}
					</Typography>
				) : (
					<>
						<Typography variant='body2' sx={{ marginBottom: '16px' }}>
							{t('memberDuplication.familyClosedText')}
						</Typography>
						<LocalDataGrid
							rows={rows}
							columns={columns}
							getRowId={(row) => row.id}
							pagination={false}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				{confirming ? (
					<>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => setConfirming(false)}
						>
							{t('button.back')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='contained'
							onClick={() =>
								selected &&
								onClose({ action: 'link', closedFamilyMember: selected })
							}
						>
							{t('button.confirm')}
						</Button>
					</>
				) : (
					<>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => onClose({ action: 'cancel' })}
						>
							{t('button.cancel')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='outlined'
							onClick={() => onClose({ action: 'skip' })}
						>
							{t('memberDuplication.continueWithoutLinking')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='contained'
							disabled={!selected}
							onClick={() => setConfirming(true)}
						>
							{t('memberDuplication.linkAndContinue')}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}
